"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ActivityTitle = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const frontend_infra_localisation_1 = require("@forto-dev/frontend-infra-localisation");
const silhouette_core_1 = require("@freighthub/silhouette-core");
const useCustomer_1 = require("../../Subscriptions/api/useCustomer");
const getCustomsStatusActivityContent_1 = require("../hooks/getCustomsStatusActivityContent");
const getPostBookingChangeActivityTitle_1 = require("../hooks/getPostBookingChangeActivityTitle");
const useDocumentActionTranslation_1 = require("../hooks/useDocumentActionTranslation");
const useMilestoneActivityTranslation_1 = require("../hooks/useMilestoneActivityTranslation");
const types_1 = require("../types");
const ActivityFeedContext_1 = require("./ActivityFeedContext");
const DocumentListContext_1 = require("./DocumentListContext");
const ManualQuoteActivity_1 = require("./ManualQuoteActivity");
const userActionTranslationMap = {
    added: 'activity_log-company_user_added',
    deleted: 'activity_log-company_user_deleted',
    confirmed: 'activity_log-company_user_confirmed',
    're-invited': 'activity_log-company_user_re_invited',
};
const useStyles = (0, silhouette_core_1.makeStyles)((theme) => ({
    textContent: {
        fontWeight: theme.typography.subtitle2.fontWeight,
    },
}));
const useActivityTitle = (isLoggedInUser, activity) => {
    const { translate, current } = (0, frontend_infra_localisation_1.useI18n)();
    const { getDocumentActionTranslation } = (0, useDocumentActionTranslation_1.useDocumentActionTranslation)();
    const { getDocumentDetails } = (0, DocumentListContext_1.useDocumentListState)();
    const { getMilestoneActivityTranslation } = (0, useMilestoneActivityTranslation_1.useMilestoneActivityTranslation)();
    const { mainEntity } = (0, ActivityFeedContext_1.useActivityFeedState)();
    const customerID = mainEntity?.type === 'customer' ? mainEntity.id : undefined;
    const { data: customerUsers } = (0, useCustomer_1.useCustomerUsers)(customerID);
    const { customField, type, actor } = activity;
    const getMessageContent = () => {
        return isLoggedInUser
            ? translate('activity_feed-message_from_self')
            : `${translate('activity_feed-message_from')} ${actor?.fullName || '-'}`;
    };
    const getAssignOpsMessageContent = () => {
        return translate('activity-feed_assigned_operations_manager', {
            user: actor?.fullName || '-',
        });
    };
    const getAssigneeMessageContent = () => {
        const assignee = customField?.['assignee'];
        const oldAssignee = customField?.['oldAssignee'];
        const assigneeType = customField?.['type'];
        let key = 'activity-feed_assignee_changed';
        if (assignee && !oldAssignee) {
            key = 'activity-feed_assignee_added';
        }
        if (!assignee && oldAssignee) {
            key = 'activity-feed_assignee_removed';
        }
        const responsibility = translate(`activity-feed-assignee-responsibility-${assigneeType}`);
        return translate(key, {
            user: assignee?.['name'] || '-',
            oldUser: oldAssignee?.['name'] || '-',
            responsibility,
        });
    };
    const manualQuoteTitle = () => {
        const quoteID = customField?.['feedV1Activity']?.['manualQuoteRequestId'];
        const status = customField?.['feedV1Activity']?.['status'] ??
            (customField?.['feedV1Activity']?.['type'] === types_1.ActivityType.ManualQuoteDeclineFeedV1 ? 'declined' : undefined);
        const statusTranslation = status && ManualQuoteActivity_1.manualQuoteTranslationMap[status];
        return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [translate('activity-feed_manual-quote_manual-quote'), " ", quoteID, ' ', status != null && !!statusTranslation && translate(statusTranslation)] }));
    };
    const TextContentMap = {
        [types_1.ActivityType.DeviationRiskInsight]: () => 'Caution against deviation risk is highlighted using this',
        [types_1.ActivityType.DeviationInsight]: () => 'Alert displaying important deviation that happened today',
        [types_1.ActivityType.Approval]: () => 'Approval needed',
        [types_1.ActivityType.OpenTasks]: () => 'Open Tasks',
        [types_1.ActivityType.MessageFeedV1]: getMessageContent,
        [types_1.ActivityType.CommunicationFeedV1]: getMessageContent,
        [types_1.ActivityType.Update]: () => 'Update for Shipment',
        [types_1.ActivityType.ShipmentContractUpdatedFeedV1]: () => 'Shipment contract number updated',
        [types_1.ActivityType.Activity]: () => '',
        [types_1.ActivityType.DocumentUploadFeedV1]: () => {
            const { role, action } = customField?.['feedV1Activity'] || {};
            const { role: fallbackRole, action: fallbackAction } = customField?.['feedV1Activity']?.['context'] || {};
            return `${getDocumentDetails(role ?? fallbackRole)?.displayName || role || 'Document'} ${getDocumentActionTranslation(action ?? fallbackAction)}`;
        },
        [types_1.ActivityType.BookingFeedV1]: () => translate('activity-feed_booking_request_placed'),
        [types_1.ActivityType.CreateShipment]: () => translate('activity-feed_booking_request_placed'),
        [types_1.ActivityType.ShipmentUpdateFeedV1]: () => translate('activity-feed_shipment_info_updated'),
        [types_1.ActivityType.UpdateShipment]: () => translate('activity-feed_shipment_info_updated'),
        [types_1.ActivityType.CustomsStatusUpdateFeedV1]: () => {
            const content = (0, getCustomsStatusActivityContent_1.getCustomsStatusActivityContent)({ activity: customField?.['feedV1Activity'] });
            return translate(content.term, content.params);
        },
        [types_1.ActivityType.CustomsDecisionUpdateFeedV1]: () => translate('activity-feed_shipment_positions_updated'),
        [types_1.ActivityType.ChangeCargoReadyDate]: () => getMilestoneActivityTranslation('cargoReady', customField),
        [types_1.ActivityType.ChangeCargoReadyDateAggregation]: () => getMilestoneActivityTranslation('cargoReady', customField),
        [types_1.ActivityType.ChangePickup]: () => getMilestoneActivityTranslation('pickup', customField),
        [types_1.ActivityType.ChangePickupAggregation]: () => getMilestoneActivityTranslation('pickup', customField),
        [types_1.ActivityType.ChangeEstimatedTimeDeparture]: () => getMilestoneActivityTranslation('departure', customField),
        [types_1.ActivityType.ChangeEstimatedTimeDepartureAggregation]: () => getMilestoneActivityTranslation('departure', customField),
        [types_1.ActivityType.ChangeArrival]: () => getMilestoneActivityTranslation('arrival', customField),
        [types_1.ActivityType.ChangeArrivalAggregation]: () => getMilestoneActivityTranslation('arrival', customField),
        [types_1.ActivityType.ChangeDelivery]: () => getMilestoneActivityTranslation('delivery', customField),
        [types_1.ActivityType.ChangeDeliveryAggregation]: () => getMilestoneActivityTranslation('delivery', customField),
        [types_1.ActivityType.SubscriptionAddFeedV1]: () => {
            const { affectedSubscriberName, affectedContact, affectedSubscriberId } = customField || {};
            return translate('activity_feed-subscription_activity_joined', {
                subscriberName: affectedSubscriberName || affectedContact?.contactPerson || affectedSubscriberId,
            });
        },
        [types_1.ActivityType.SubscriptionRemoveFeedV1]: () => {
            const { affectedSubscriberName, affectedContact, affectedSubscriberId } = customField || {};
            return translate('activity_feed-subscription_activity_left', {
                subscriberName: affectedSubscriberName || affectedContact?.contactPerson || affectedSubscriberId,
            });
        },
        [types_1.ActivityType.BookingUpdateFeedV1]: () => (0, getPostBookingChangeActivityTitle_1.getPostBookingChangeActivityTitle)(customField?.['feedV1Activity']),
        [types_1.ActivityType.UpdatePostBooking]: () => (0, getPostBookingChangeActivityTitle_1.getPostBookingChangeActivityTitle)(customField),
        [types_1.ActivityType.RateUpdateAppliedFeedV1]: () => 'Rate update applied',
        [types_1.ActivityType.ChangeOperationManagerFeedV1]: getAssignOpsMessageContent,
        [types_1.ActivityType.ChangeOriginOperationManagerFeedV1]: getAssignOpsMessageContent,
        [types_1.ActivityType.ChangeAssignee]: getAssigneeMessageContent,
        [types_1.ActivityType.CompanyUserUpdateFeedV1]: () => {
            const action = customField?.['feedV1Activity']?.['action'];
            const actionTranslation = userActionTranslationMap[action];
            const targetUserID = customField?.['feedV1Activity']?.['targetCompanyUser_id'];
            const matchingUser = customerUsers?.find((user) => user.id === targetUserID);
            const targetUserName = matchingUser ? `${matchingUser.firstName} ${matchingUser.lastName}` : targetUserID;
            return translate(actionTranslation, { targetUserName });
        },
        [types_1.ActivityType.ManualQuoteFeedV1]: manualQuoteTitle,
        [types_1.ActivityType.ManualQuoteDeclineFeedV1]: manualQuoteTitle,
    };
    const renderedContent = customField?.['renderedContent'];
    if (renderedContent) {
        const languageContent = renderedContent?.[current] || renderedContent?.['en'];
        return languageContent?.title || '';
    }
    if (customField?.['ruleID']) {
        return translate(`activity_feed-${[customField['ruleID']]}-title`);
    }
    return TextContentMap[type]?.() || '';
};
const ActivityTitle = ({ activity, isLoggedInUser }) => {
    const classes = useStyles();
    const activityTitle = useActivityTitle(isLoggedInUser, activity);
    return ((0, jsx_runtime_1.jsxs)(silhouette_core_1.Typography, { variant: "body2", color: "textSecondary", className: classes.textContent, children: [activityTitle || activity.type.replace(/[A-Z]/g, (letter) => ` ${letter}`), ' '] }));
};
exports.ActivityTitle = ActivityTitle;
//# sourceMappingURL=ActivityTitle.js.map